import React, { useState } from "react";

import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import { ADD_INVENTORY_ITEM_MUTATION } from "src/constants/graphql/mutations";
import AddItemSearchModal from "./AddItemSearchModal";
import AddItemResultsModal from "./AddItemResultsModal";
import AddItemQuantityModal from "./AddItemQuantityModal";

const STEP_SEARCH = "search";
const STEP_RESULTS = "results";
const STEP_QUANTITY = "quantity";

export default function AddItemFlow({ projectId, inventoryRequestId, processNewItem, onClose }) {
  const [step, setStep] = useState(STEP_SEARCH);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchProjectId, setSearchProjectId] = useState(projectId || null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { notifySuccess, notifyError } = useNotification();
  const [addInventoryItem, { loading }] = useMutation(ADD_INVENTORY_ITEM_MUTATION);

  // term: string, chosenProjectId: string|null (null = all projects)
  const handleSearch = (term, chosenProjectId) => {
    setSearchTerm(term);
    setSearchProjectId(chosenProjectId);
    setStep(STEP_RESULTS);
  };

  const resetToSearch = () => {
    setSearchTerm("");
    setSelectedAsset(null);
    setStep(STEP_SEARCH);
  };

  const callMutation = (asset, quantity) => {
    let variables = {
      quantity,
      projectId,
      assetId: asset.id,
      inventoryRequestId,
    };

    if (processNewItem) {
      variables = processNewItem(variables);
    }

    return addInventoryItem({
      variables,
      onSuccess: () => {
        notifySuccess("Item successfully added to order");
        resetToSearch();
      },
      onFailure: errors => {
        notifyError(errors.quantity || errors.asset || errors.submit || "Failed to add item");
      },
    });
  };

  const handleSelectAsset = asset => {
    if (asset.availableQuantity === 1) {
      callMutation(asset, 1);
    } else {
      setSelectedAsset(asset);
      setStep(STEP_QUANTITY);
    }
  };

  const handleConfirmQuantity = quantity => {
    callMutation(selectedAsset, quantity);
  };

  if (step === STEP_SEARCH) {
    return (
      <AddItemSearchModal
        onSearch={handleSearch}
        onClose={onClose}
        defaultProjectId={projectId}
      />
    );
  }

  if (step === STEP_RESULTS) {
    return (
      <AddItemResultsModal
        searchTerm={searchTerm}
        projectId={searchProjectId}
        onSelect={handleSelectAsset}
        onBack={() => setStep(STEP_SEARCH)}
        onClose={onClose}
        loading={loading}
      />
    );
  }

  if (step === STEP_QUANTITY) {
    return (
      <AddItemQuantityModal
        asset={selectedAsset}
        onConfirm={handleConfirmQuantity}
        onBack={() => setStep(STEP_RESULTS)}
        onClose={onClose}
        loading={loading}
      />
    );
  }

  return null;
}
